import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import api from '../../../utils/api';
import { showToast } from '../../../Components/ToastNotifications';

// Versi mentor dari Pages/Admin/Attendances/AttendancesParticipantDetail.jsx,
// ditambah satu aksi yang tidak ada di sisi admin: sinkronkan skor kehadiran
// ke evaluasi final BCF (pengganti alur "verifikasi" lama yang sudah tidak
// dipakai — lihat CLPMentorSyncAttendanceScoreUseCase).
const getStatusLabel = (status) => {
  switch (status) {
    case 'ON_TIME':
      return 'Hadir';
    case 'LATE_TOLERANCE':
      return 'Hadir (Toleransi)';
    case 'PENDING_APPROVAL':
      return 'Menunggu Persetujuan';
    case 'EARLY_EXIT_PENDING':
      return 'Keluar Lebih Awal';
    case 'ABSENT':
      return 'Belum Absen';
    case 'LATE':
      return 'Terlambat';
    default:
      return status || '-';
  }
};

const getRequestTypeLabel = (type) => {
  switch (type) {
    case 'LATE_CLOCK_IN':
      return 'Keterlambatan Clock In';
    case 'LATE_CLOCK_OUT':
      return 'Keterlambatan Clock Out';
    case 'MISSED_CLOCK_IN':
      return 'Lupa Clock In';
    case 'MISSED_CLOCK_OUT':
      return 'Lupa Clock Out';
    case 'EARLY_CLOCK_OUT':
      return 'Clock Out Lebih Awal';
    default:
      return type || '-';
  }
};

const getRequestTypeBadge = (type) => {
  if (type === 'LATE_CLOCK_IN') {
    return { className: 'bg-warning-subtle text-warning', icon: 'bi-clock-history' };
  }
  if (type === 'EARLY_CLOCK_OUT') {
    return { className: 'bg-info-subtle text-info', icon: 'bi-box-arrow-right' };
  }
  return { className: 'bg-secondary-subtle text-secondary', icon: 'bi-question-circle' };
};

const getToleranceStatusLabel = (status) => {
  if (status === 'APPROVED') return 'Disetujui';
  if (status === 'REJECTED') return 'Ditolak';
  return 'Menunggu';
};

const getRequestStatusBadgeClass = (status) => {
  if (status === 'APPROVED') return 'bg-success-subtle text-success';
  if (status === 'REJECTED') return 'bg-danger-subtle text-danger';
  return 'bg-warning-subtle text-warning';
};

const RANGE_OPTIONS = [
  { value: 'day', label: 'Hari' },
  { value: 'week', label: 'Minggu' },
  { value: 'month', label: 'Bulan' },
];

const AttendancesParticipantDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const participantName = location.state
    ? `${location.state.firstName || ''} ${location.state.lastName || ''}`.trim()
    : '';

  const [quota, setQuota] = useState(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(true);
  const [showUsageDays, setShowUsageDays] = useState(false);

  const [historyRange, setHistoryRange] = useState('day');
  const [historyDate, setHistoryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [history, setHistory] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [reviewingId, setReviewingId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchQuota = useCallback(async () => {
    setIsLoadingQuota(true);
    try {
      const response = await api.getMentorAttendanceQuota(userId);
      setQuota(response);
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal memuat kuota mentee', 'danger');
    } finally {
      setIsLoadingQuota(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const fetchHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      const response = await api.getMentorAttendanceHistory(userId, {
        range: historyRange,
        date: historyDate,
      });
      setHistory(response);
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal memuat riwayat kehadiran', 'danger');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [userId, historyRange, historyDate]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleReviewTolerance = async (requestId, status) => {
    if (!requestId) return;
    try {
      setReviewingId(requestId);
      await api.reviewMentorTolerance(requestId, { status });
      showToast(`Pengajuan berhasil ${status === 'APPROVED' ? 'disetujui' : 'ditolak'}.`, 'success');
      fetchHistory();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal memperbarui status pengajuan', 'danger');
    } finally {
      setReviewingId(null);
    }
  };

  const handleSyncScore = async () => {
    try {
      setIsSyncing(true);
      const result = await api.syncMentorAttendanceScore(userId);
      showToast(`Skor kehadiran disinkronkan: ${result.score}%.`, 'success');
      fetchQuota();
    } catch (err) {
      console.error(err);
      showToast(err.message || 'Gagal menyinkronkan skor kehadiran', 'danger');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div className='card card-body border-0 shadow-sm rounded-4 p-4'>
      {/* Header Section */}
      <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4'>
        <div className='d-flex align-items-center gap-3'>
          <button
            className='btn btn-light rounded-circle shadow-sm d-flex align-items-center justify-content-center'
            style={{ width: '40px', height: '40px' }}
            onClick={() => navigate(-1)}
          >
            <i className='bi bi-arrow-left text-dark fs-5'></i>
          </button>
          <div
            className='bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3'
            style={{ width: '40px', height: '40px' }}
          >
            <i className='bi bi-speedometer2 text-primary fs-5'></i>
          </div>
          <div>
            <h1 className='h5 fw-bold mb-0 text-dark'>
              Kuota &amp; Riwayat Kehadiran
              {participantName && <span className='text-muted fw-normal'> &mdash; {participantName}</span>}
            </h1>
            <p className='text-muted mb-0 d-none d-lg-block' style={{ fontSize: '0.75rem' }}>
              Ringkasan kuota toleransi keterlambatan dan rekam jejak kehadiran harian mentee.
            </p>
          </div>
        </div>
        <button
          type='button'
          className='btn btn-primary btn-sm rounded-pill px-3 fw-bold flex-shrink-0'
          disabled={isSyncing}
          onClick={handleSyncScore}
          title='Hitung ulang skor kehadiran dari sesi clock-in/out dan simpan ke evaluasi final (bobot ATT)'
        >
          {isSyncing ? (
            <span className='spinner-border spinner-border-sm me-2' role='status'></span>
          ) : (
            <i className='bi bi-arrow-repeat me-1'></i>
          )}
          Sinkronkan Skor Kehadiran
        </button>
      </div>

      <div className='row g-4'>
        {/* Kuota Toleransi Keterlambatan */}
        <div className='col-12'>
          <div className='card border border-light-subtle rounded-4'>
            <div className='card-header bg-white border-0 p-4 pb-0'>
              <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                <i className='bi bi-speedometer2 text-primary'></i>
                Kuota Toleransi Keterlambatan
              </h6>
            </div>
            <div className='card-body p-4'>
              {isLoadingQuota || !quota ? (
                <div className='placeholder-glow'>
                  <span className='placeholder col-4 rounded'></span>
                </div>
              ) : (
                <>
                  <div className='row g-3 mb-2'>
                    <div className='col-md-6'>
                      <div className='bg-light p-3 rounded-4 h-100'>
                        <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.65rem' }}>
                          Terpakai / Total Kuota
                        </span>
                        <h4 className='fw-bold mb-0 text-dark'>
                          {quota.usedQuota} / {quota.totalQuota}
                          <span className='text-muted small fw-normal ms-2'>({quota.remainingQuota} tersisa)</span>
                        </h4>
                      </div>
                    </div>
                    {!!quota.totalBatchDays && (
                      <div className='col-md-6'>
                        <div className='bg-light p-3 rounded-4 h-100'>
                          <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.65rem' }}>
                            Sudah Terisi
                          </span>
                          <h4 className='fw-bold mb-0 text-dark'>
                            {quota.filledDays} / {quota.totalBatchDays}
                            <span className='text-muted small fw-normal ms-2'>hari periode batch</span>
                          </h4>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-2'>
                    {quota.usageDays.length > 0 && (
                      <button
                        type='button'
                        className='btn btn-outline-secondary btn-sm rounded-pill px-3 fw-bold flex-shrink-0'
                        onClick={() => setShowUsageDays((prev) => !prev)}
                      >
                        {showUsageDays ? 'Sembunyikan' : 'Lihat'} Tanggal Terpakai
                        <i className={`bi bi-chevron-${showUsageDays ? 'up' : 'down'} ms-1`}></i>
                      </button>
                    )}
                  </div>
                  {quota.usageDays.length === 0 ? (
                    <p className='text-muted small mb-0'>Belum ada kuota yang terpakai pada batch ini.</p>
                  ) : (
                    showUsageDays && (
                      <div className='d-flex flex-column gap-2 mt-3'>
                        {quota.usageDays.map((usage) => (
                          <div key={usage.sessionId} className='bg-light rounded-4 p-3'>
                            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2'>
                              <span className='fw-bold text-dark small'>
                                {format(new Date(usage.sessionDate), 'dd MMMM yyyy', { locale: localeId })}
                              </span>
                              <span className='badge bg-info-subtle text-info rounded-pill px-2 py-1 fw-semibold extra-small'>
                                Kuota Terpakai
                              </span>
                            </div>
                            <div className='row g-2 mb-2'>
                              <div className='col-sm-4'>
                                <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.6rem' }}>
                                  Clock In
                                </span>
                                <span className='small fw-medium text-dark'>
                                  {usage.clockInAt ? format(new Date(usage.clockInAt), 'HH:mm') : '--:--'}
                                </span>
                              </div>
                              <div className='col-sm-4'>
                                <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.6rem' }}>
                                  Clock Out
                                </span>
                                <span className='small fw-medium text-dark'>
                                  {usage.clockOutAt ? format(new Date(usage.clockOutAt), 'HH:mm') : '--:--'}
                                  {usage.clockOutStatus === 'EARLY_EXIT_PENDING' && <span className='text-warning ms-1'>(Pulang Awal)</span>}
                                </span>
                              </div>
                              <div className='col-sm-4'>
                                <span className='text-muted extra-small fw-bold text-uppercase d-block' style={{ fontSize: '0.6rem' }}>
                                  Logbook
                                </span>
                                <span className={`small fw-medium ${usage.hasLogbook ? 'text-success' : 'text-muted'}`}>
                                  {usage.hasLogbook ? 'Terisi' : 'Belum diisi'}
                                </span>
                              </div>
                            </div>
                            {usage.toleranceRequests.length > 0 && (
                              <div className='d-flex flex-column gap-1 pt-2 border-top'>
                                {usage.toleranceRequests.map((request) => (
                                  <div key={request.id} className='d-flex align-items-start justify-content-between gap-2'>
                                    <div>
                                      <span
                                        className={`badge rounded-pill px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 mb-1 ${getRequestTypeBadge(request.requestType).className}`}
                                        style={{ fontSize: '0.65rem' }}
                                      >
                                        <i className={`bi ${getRequestTypeBadge(request.requestType).icon}`}></i>
                                        {getRequestTypeLabel(request.requestType)}
                                      </span>
                                      <span className='text-muted extra-small d-block'>{request.reason}</span>
                                    </div>
                                    <span
                                      className={`badge rounded-pill px-2 py-1 fw-semibold flex-shrink-0 ${getRequestStatusBadgeClass(request.status)}`}
                                      style={{ fontSize: '0.65rem' }}
                                    >
                                      {getToleranceStatusLabel(request.status)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Riwayat Kehadiran */}
        <div className='col-12'>
          <div className='card border border-light-subtle rounded-4 overflow-hidden'>
            <div className='card-header bg-white border-0 p-4 pb-0'>
              <div className='d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2'>
                <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                  <i className='bi bi-calendar-range text-primary'></i>
                  Riwayat Kehadiran
                </h6>
                <div className='d-flex align-items-center gap-2'>
                  <div className='btn-group btn-group-sm' role='group'>
                    {RANGE_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type='button'
                        className={`btn fw-bold ${historyRange === opt.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setHistoryRange(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    type='date'
                    className='form-control form-control-sm rounded-3'
                    style={{ width: '150px' }}
                    value={historyDate}
                    onChange={(e) => setHistoryDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className='card-body p-4'>
              {isLoadingHistory ? (
                <div className='d-flex justify-content-center py-4'>
                  <div className='spinner-border spinner-border-sm text-primary' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : !history || history.sessions.length === 0 ? (
                <div className='text-center py-4'>
                  <i className='bi bi-inbox text-muted fs-2 mb-2 d-block opacity-25'></i>
                  <p className='text-muted small mb-0'>Tidak ada rekaman kehadiran pada rentang ini.</p>
                </div>
              ) : (
                <div className='table-responsive border rounded-4 bg-white overflow-hidden'>
                  <table className='table table-hover align-middle mb-0' style={{ fontSize: '0.85rem' }}>
                    <thead className='table-light'>
                      <tr>
                        <th className='ps-3 py-3 text-muted fw-bold'>Tanggal</th>
                        <th className='py-3 text-muted fw-bold text-center'>Clock In</th>
                        <th className='py-3 text-muted fw-bold text-center'>Clock Out</th>
                        <th className='pe-3 py-3 text-muted fw-bold' style={{ minWidth: '260px' }}>Pengajuan &amp; Alasan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.sessions.map((session) => (
                        <tr key={session.id}>
                          <td className='ps-3 py-3 fw-medium text-dark'>
                            {format(new Date(session.sessionDate), 'dd MMM yyyy', { locale: localeId })}
                          </td>
                          <td className='py-3 text-center'>
                            <span className='d-block'>
                              {session.clockInAt ? format(new Date(session.clockInAt), 'HH:mm') : '--:--'}
                            </span>
                            <span className='extra-small text-muted'>{getStatusLabel(session.clockInStatus)}</span>
                          </td>
                          <td className='py-3 text-center'>
                            <span className='d-block'>
                              {session.clockOutAt ? format(new Date(session.clockOutAt), 'HH:mm') : '--:--'}
                            </span>
                            <span className='extra-small text-muted'>
                              {session.clockOutStatus ? getStatusLabel(session.clockOutStatus) : '-'}
                            </span>
                          </td>
                          <td className='pe-3 py-3'>
                            {!session.toleranceRequests || session.toleranceRequests.length === 0 ? (
                              <span className='text-muted extra-small'>-</span>
                            ) : (
                              <div className='d-flex flex-column gap-2'>
                                {session.toleranceRequests.map((request) => (
                                  <div key={request.id} className='bg-light rounded-3 p-2'>
                                    <div className='d-flex align-items-center justify-content-between gap-2 mb-1'>
                                      <span
                                        className={`badge rounded-pill px-2 py-1 fw-semibold d-inline-flex align-items-center gap-1 ${getRequestTypeBadge(request.requestType).className}`}
                                        style={{ fontSize: '0.65rem' }}
                                      >
                                        <i className={`bi ${getRequestTypeBadge(request.requestType).icon}`}></i>
                                        {getRequestTypeLabel(request.requestType)}
                                      </span>
                                      <span
                                        className={`badge rounded-pill px-2 py-1 fw-semibold ${getRequestStatusBadgeClass(request.status)}`}
                                        style={{ fontSize: '0.65rem' }}
                                      >
                                        {getToleranceStatusLabel(request.status)}
                                      </span>
                                    </div>
                                    <p className='text-muted extra-small mb-0 text-break'>{request.reason}</p>
                                    {request.status !== 'PENDING' && request.reviewerName && (
                                      <p className='text-muted extra-small mb-0 mt-1 fst-italic'>
                                        Ditinjau oleh {request.reviewerName}
                                      </p>
                                    )}
                                    {request.status === 'PENDING' && (
                                      <div className='d-flex gap-2 mt-2'>
                                        <button
                                          type='button'
                                          className='btn btn-outline-danger btn-sm rounded-pill px-2 py-0 fw-bold'
                                          style={{ fontSize: '0.7rem' }}
                                          disabled={reviewingId === request.id}
                                          onClick={() => handleReviewTolerance(request.id, 'REJECTED')}
                                        >
                                          Tolak
                                        </button>
                                        <button
                                          type='button'
                                          className='btn btn-success btn-sm rounded-pill px-2 py-0 fw-bold'
                                          style={{ fontSize: '0.7rem' }}
                                          disabled={reviewingId === request.id}
                                          onClick={() => handleReviewTolerance(request.id, 'APPROVED')}
                                        >
                                          Setujui
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .extra-small { font-size: 0.7rem; }
        .transition-all { transition: all 0.2s ease; }
      `}</style>
    </div>
  );
};

export default AttendancesParticipantDetail;
